import { useModalPanels } from "@libs/modal-panels/use-modal-panels";
import type { iMultiChannelCampaignVariableConfig } from "@sdk/chat-widgets/chat-widgets.models";
import { Button, Form, Input, Select, Switch } from "antd";
import ManageMultiChannelVariables from "./manage-multi-channel-variables/manage-multi-channel-variables.modal-registry";

const VARIABLES_FIELD_NAME = [
  "configurations",
  "multiChannelConfig",
  "variables",
] as const;

/** Hidden control so Form stores the variable object; form.getFieldsValue() will include it */
const HiddenVariableField = (_: {
  value?: iMultiChannelCampaignVariableConfig;
  onChange?: (v: iMultiChannelCampaignVariableConfig) => void;
}) => null;

/**
 * Rendered only when the Configuration Editor modal is open (inside its Form).
 * Uses Form.useFormInstance() here so the form instance is the one from
 * ConfigurationEditorInner, not from any parent on the page.
 */
export const MultiChannelWidgetSettingsInner = () => {
  const form = Form.useFormInstance();
  const { triggerTempModal } = useModalPanels();

  const isEnabled = form.getFieldValue([
    "configurations",
    "multiChannelConfig",
    "isEnabled",
  ]);
  const variables: iMultiChannelCampaignVariableConfig[] =
    form.getFieldValue(["configurations", "multiChannelConfig", "variables"]) ??
    [];
  const variableCount = Array.isArray(variables) ? variables.length : 0;

  const openVariablesModal = () => {
    triggerTempModal(ManageMultiChannelVariables, {
      initialVariables:
        form.getFieldValue([
          "configurations",
          "multiChannelConfig",
          "variables",
        ]) ?? [],
      onSave: (newVariables: iMultiChannelCampaignVariableConfig[]) => {
        form.setFieldsValue({
          configurations: {
            multiChannelConfig: {
              variables: newVariables,
            },
          },
        });
        form.setFields([
          {
            name: [...VARIABLES_FIELD_NAME],
            touched: true,
          },
        ]);
      },
    });
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <Form.Item
        label="Card Title"
        name={["configurations", "multiChannelConfig", "cardTitle"]}
        rules={[{ required: true, message: "Please enter card title" }]}
      >
        <Input placeholder="e.g. Contact Us / Support Channels" />
      </Form.Item>

      <Form.Item
        label="Card Description"
        name={["configurations", "multiChannelConfig", "cardDescription"]}
      >
        <Input.TextArea
          placeholder="e.g. Choose your preferred way to connect with us"
          rows={2}
        />
      </Form.Item>

      {/* Form.List registers variables with the form so getFieldsValue() includes them */}
      <Form.List name={[...VARIABLES_FIELD_NAME]}>
        {(fields) => (
          <>
            <Form.Item label="Campaign Variables" className="mb-4">
              <div className="flex flex-row items-center justify-end gap-2">
                <span className="text-gray-600 dark:text-gray-400">
                  {variableCount} variable{variableCount !== 1 ? "s" : ""}{" "}
                  configured
                </span>
                <Button
                  type="text"
                  icon={<i className="ri-settings-3-line" />}
                  onClick={(e) => {
                    e.preventDefault();
                    openVariablesModal();
                  }}
                />
              </div>
            </Form.Item>
            {fields.map((field) => (
              <Form.Item
                {...field}
                key={field.key}
                name={[field.name]}
                noStyle
                hidden
              >
                <HiddenVariableField />
              </Form.Item>
            ))}
          </>
        )}
      </Form.List>

      <Form.Item
        label="Greetings Message"
        name={["configurations", "multiChannelConfig", "greetingsMessage"]}
        help={
          <div className="mb-4">
            Shown to all visitors when the automated chat greeting is off. Leave
            blank to disable.
          </div>
        }
      >
        <Input.TextArea
          placeholder="Eg: Our team is online and ready to help you plan your next holiday. How can we help you today?"
          rows={3}
        />
      </Form.Item>

      <Form.Item
        name={["configurations", "multiChannelConfig", "autoTriggerGreetings"]}
        label={"Auto Trigger Greetings"}
        rules={[]}
        valuePropName="checked"
      >
        <Switch size="small" />
      </Form.Item>

      <Form.List name={["configurations", "multiChannelConfig", "channels"]}>
        {(fields, { add, remove }) => (
          <div className="flex flex-col gap-2">
            {fields.map((field, index) => (
              <div
                key={field.key}
                className="border border-gray-200 dark:border-gray-700 p-8 rounded-lg relative"
              >
                <Button
                  type="text"
                  icon={<i className="ri-delete-bin-line" />}
                  className="absolute right-0 top-0"
                  onClick={() => remove(index)}
                />

                <Form.Item
                  {...field}
                  label="Channel Type"
                  name={[field.name, "type"]}
                  rules={[
                    {
                      required: true,
                      message: "Please select channel type",
                    },
                  ]}
                >
                  <Select
                    onChange={(value) => {
                      const defaultLabels = {
                        WHATSAPP: "WhatsApp",
                        EMAIL: "Email",
                        LIVE_CHAT: "Live Chat",
                        PHONE: "Phone",
                        URL: "Custom URL",
                      };
                      form.setFieldsValue({
                        configurations: {
                          multiChannelConfig: {
                            channels: {
                              [field.name]: {
                                label: defaultLabels[value],
                              },
                            },
                          },
                        },
                      });
                    }}
                  >
                    <Select.Option value="WHATSAPP">WhatsApp</Select.Option>
                    <Select.Option value="EMAIL">Email</Select.Option>
                    <Select.Option value="LIVE_CHAT">Live Chat</Select.Option>
                    <Select.Option value="PHONE">Phone</Select.Option>
                    <Select.Option value="URL">Custom URL</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  {...field}
                  label="Label"
                  name={[field.name, "label"]}
                  rules={[{ required: true, message: "Please enter label" }]}
                >
                  <Input placeholder="e.g. WhatsApp Support" />
                </Form.Item>

                <Form.Item
                  {...field}
                  label="Description"
                  name={[field.name, "description"]}
                >
                  <Input.TextArea
                    placeholder="Enter a description for this channel"
                    rows={2}
                  />
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const type = getFieldValue([
                      "configurations",
                      "multiChannelConfig",
                      "channels",
                      field.name,
                      "type",
                    ]);

                    if (type === "WHATSAPP") {
                      return (
                        <>
                          <Form.Item
                            {...field}
                            label="Phone Number"
                            name={[field.name, "phoneNumber"]}
                            rules={[
                              {
                                required: true,
                                message: "Please enter phone number",
                              },
                            ]}
                          >
                            <Input placeholder="e.g. +1234567890" />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            label="Message Template"
                            name={[field.name, "messageTemplate"]}
                          >
                            <Input.TextArea placeholder="Enter default message template" />
                          </Form.Item>
                        </>
                      );
                    }

                    if (type === "EMAIL") {
                      return (
                        <Form.Item
                          {...field}
                          label="Email Address"
                          name={[field.name, "email"]}
                          rules={[
                            {
                              required: true,
                              message: "Please enter email address",
                            },
                            // {
                            //   type: "email",
                            //   message: "Please enter valid email address",
                            // },
                          ]}
                        >
                          <Input placeholder="e.g. support@example.com" />
                        </Form.Item>
                      );
                    }

                    if (type === "PHONE") {
                      return (
                        <Form.Item
                          {...field}
                          label="Phone Number"
                          name={[field.name, "phoneNumber"]}
                          rules={[
                            {
                              required: true,
                              message: "Please enter phone number",
                            },
                          ]}
                        >
                          <Input placeholder="e.g. +1234567890" />
                        </Form.Item>
                      );
                    }

                    if (type === "URL") {
                      return (
                        <Form.Item
                          {...field}
                          label="URL"
                          name={[field.name, "url"]}
                          rules={[
                            { required: true, message: "Please enter URL" },
                            // {
                            //   type: "url",
                            //   message: "Please enter valid URL",
                            // },
                          ]}
                        >
                          <Input placeholder="e.g. https://example.com/support" />
                        </Form.Item>
                      );
                    }

                    return null;
                  }}
                </Form.Item>
              </div>
            ))}

            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<i className="ri-add-line" />}
              className="my-4"
            >
              Add Channel
            </Button>
          </div>
        )}
      </Form.List>
    </>
  );
};
